import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import type { OrganizationPublic, SchoolTypePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

// School create form -- per checklist: name, abbreviation, location, latitude, longitude

export default function SchoolCreatePage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [location, setLocation] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [organizations, setOrganizations] = useState<OrganizationPublic[]>([]);
  const [schoolTypeId, setSchoolTypeId] = useState("S");
  const [schoolTypes, setSchoolTypes] = useState<SchoolTypePublic[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getOrganizations().then((res) => {
      setOrganizations(res.organizations);
      // Auto-select first org
      if (res.organizations.length > 0) {
        setOrganizationId(res.organizations[0].id);
      }
    });
    api.getSchoolTypes().then((res) => setSchoolTypes(res.school_types));
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await api.createSchool({
        organization_id: organizationId,
        school_type_id: schoolTypeId || null,
        name,
        abbreviation: abbreviation || null,
        website_url: websiteUrl || null,
        location: location || null,
        latitude: latitude ? parseFloat(latitude) : null,
        longitude: longitude ? parseFloat(longitude) : null,
      });
      navigate("/schools");
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to create school");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-page="school-create" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Add School</h1>
        <p className="text-muted-foreground">Create a new school</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">School Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="school-create">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            {organizations.length > 1 && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="organization">Organization</Label>
                <select
                  id="organization"
                  data-field="organization"
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="schoolType">School Type</Label>
              <select
                id="schoolType"
                data-field="school-type"
                value={schoolTypeId}
                onChange={(e) => setSchoolTypeId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">-- None --</option>
                {schoolTypes.map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">School Name</Label>
              <Input
                id="name"
                data-field="name"
                placeholder="e.g. Hoërskool Menlopark"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="abbreviation">Abbreviation</Label>
              <Input
                id="abbreviation"
                data-field="abbreviation"
                placeholder="e.g. h/s Menlopark"
                value={abbreviation}
                onChange={(e) => setAbbreviation(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="website_url">Website</Label>
              <Input
                id="website_url"
                data-field="website-url"
                type="url"
                placeholder="e.g. https://menlopark.co.za"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                data-field="location"
                placeholder="e.g. Pretoria, Gauteng"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="latitude">Latitude</Label>
                <Input
                  id="latitude"
                  data-field="latitude"
                  type="number"
                  step="any"
                  placeholder="e.g. -25.7646"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="longitude">Longitude</Label>
                <Input
                  id="longitude"
                  data-field="longitude"
                  type="number"
                  step="any"
                  placeholder="e.g. 28.2583"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting || !organizationId}>
                {submitting ? "Creating..." : "Create School"}
              </Button>
              <Link to="/schools" className={buttonVariants({ variant: "outline" })} data-action="cancel">
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
